import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const SVG_NS = 'http://www.w3.org/2000/svg';
const X_COORDS_BIG = [77, 142, 207, 272, 337, 402, 467, 532];
const X_COORDS_SMALL = [82, 179, 276, 373];

function wrapPathsInGroups(paths) {
  return paths.map((path) => {
    const bbox = path.getBBox();
    const cx = bbox.x + bbox.width / 2;
    const cy = bbox.y + bbox.height / 2;

    const g = document.createElementNS(SVG_NS, 'g');
    g.classList.add('sf-wrap');
    path.parentNode.insertBefore(g, path);
    g.appendChild(path);

    return { g, cx, cy };
  });
}

function getGrid(wrapped, isBig) {
  const xCoords = isBig ? X_COORDS_BIG : X_COORDS_SMALL;
  const totalH = isBig ? 609 : 456;
  const totalRows = isBig ? 9 : 4;

  return wrapped.map(({ g, cx, cy }) => {
    let col = 0;
    let minDx = Infinity;
    xCoords.forEach((x, i) => {
      const dx = Math.abs(cx - x);
      if (dx < minDx) { minDx = dx; col = i; }
    });
    const row = Math.round((cy / totalH) * (totalRows - 1));
    return { g, col, row };
  });
}

function startLoop(grid) {
  const wrappers = grid.map((e) => e.g);

  grid.forEach(({ g }) => {
    const dur = (2.5 + Math.random() * 2.0).toFixed(2);
    const delay = (Math.random() * 3.0).toFixed(2);
    g.style.setProperty('--sf-dur', `${dur}s`);
    g.style.setProperty('--sf-delay', `${delay}s`);
    g.classList.add('sf-looping');
  });

  return {
    pause() {
      wrappers.forEach((g) => { g.style.animationPlayState = 'paused'; });
    },
    resume() {
      wrappers.forEach((g) => { g.style.animationPlayState = ''; });
    },
  };
}

function initSigilFigureAnimation() {
  const containers = document.querySelectorAll('[data-sigil-figure]');
  if (!containers.length) return;

  containers.forEach((container) => {
    if (container.dataset.sigilFigureInitialized) return;

    const svg = container.querySelector('svg');
    if (!svg) return;

    const paths = Array.from(svg.querySelectorAll('path'));
    if (!paths.length) return;

    container.dataset.sigilFigureInitialized = 'true';

    const isBig = svg.getAttribute('width') === '609';
    const wrapped = wrapPathsInGroups(paths);
    svg.classList.add('sf-ready');

    const grid = getGrid(wrapped, isBig);
    const wrappers = grid.map((e) => e.g);

    gsap.set(wrappers, { opacity: 0 });

    const diagMap = new Map();
    grid.forEach((entry) => {
      const key = entry.col + entry.row;
      if (!diagMap.has(key)) diagMap.set(key, []);
      diagMap.get(key).push(entry.g);
    });
    const diagonals = [...diagMap.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([, gs]) => gs);

    const INTRO_DURATION = 1.4;
    const DIAG_STEP = 0.08;
    const totalIntroDur = INTRO_DURATION + (diagonals.length - 1) * DIAG_STEP;

    let loop = null;
    let loopStarted = false;

    const introTl = gsap.timeline({
      scrollTrigger: {
        trigger: container,
        start: 'top 85%',
        once: true,
      },
      onComplete() {
        if (!loopStarted) {
          loopStarted = true;
          loop = startLoop(grid);
        }
      },
    });

    diagonals.forEach((diagGs, i) => {
      introTl.to(diagGs, {
        opacity: 1,
        duration: INTRO_DURATION,
        ease: 'sine.inOut',
      }, i * DIAG_STEP);
    });

    const visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!loopStarted || !loop) return;
          if (entry.isIntersecting) {
            loop.resume();
          } else {
            loop.pause();
          }
        });
      },
      { rootMargin: '150px 0px 150px 0px', threshold: 0 }
    );

    visibilityObserver.observe(container);
  });
}

window.initSigilFigureAnimation = initSigilFigureAnimation;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSigilFigureAnimation);
} else {
  initSigilFigureAnimation();
}
